import * as React from 'react';
import { t } from '@reos/i18n';
import { Link } from '@tanstack/react-router';
import { baseUrl } from '../lib/api.js';
import { listAuditBatches, retryFailedBatchItems, type AuditRunBatchDto, type AuditRunBatchStatus } from '../lib/audit-api.js';
import { AUDIT_RUN_STATUS_CAPTIONS } from '../lib/texts.js';
import { captionOrPreparing } from '../lib/broker-text.js';
import { failureReasonSentence } from '../lib/failure-reason.js';
import { ListTable } from '../components/list-table.js';
import { auditHumanRow, isUserChange } from '../lib/audit-human.js';

interface AuditEvent {
  readonly id: string;
  readonly seq: string;
  readonly tenantId: string | null;
  readonly occurredAt: string;
  readonly actorType: string;
  readonly actorId: string | null;
  readonly actorRole: string | null;
  readonly action: string;
  readonly entityType: string | null;
  readonly entityId: string | null;
  readonly before: unknown;
  readonly after: unknown;
  readonly reason: string | null;
  readonly traceId: string | null;
  readonly approvalId: string | null;
  readonly onBehalfOf: string | null;
  readonly prevHash: string;
  readonly hash: string;
}

interface Anchor {
  readonly id: string;
  readonly year: number;
  readonly month: number;
  readonly anchorHash: string;
  readonly prevAnchorHash: string;
  readonly eventCount: number;
  readonly wormPath: string | null;
}

const BATCH_STATUS_LABELS: Readonly<Record<AuditRunBatchStatus, string>> = {
  QUEUED: t('webAdmin.auditBatches.statusQueued', { locale: 'cs' }),
  RUNNING: t('webAdmin.auditBatches.statusRunning', { locale: 'cs' }),
  DONE: t('webAdmin.auditBatches.statusDone', { locale: 'cs' }),
  FAILED: t('webAdmin.auditBatches.statusFailed', { locale: 'cs' }),
  PARTIAL: t('webAdmin.auditBatches.statusPartial', { locale: 'cs' }),
};

function AuditBatchesSection(): React.JSX.Element {
  const [batches, setBatches] = React.useState<readonly AuditRunBatchDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [retryingId, setRetryingId] = React.useState<string | null>(null);
  const [retryError, setRetryError] = React.useState<{ readonly batchId: string; readonly message: string } | null>(null);

  const fetchBatches = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setBatches(await listAuditBatches());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void fetchBatches();
  }, [fetchBatches]);

  async function retryBatch(batchId: string): Promise<void> {
    setRetryingId(batchId);
    setRetryError(null);
    try {
      const updated = await retryFailedBatchItems(batchId);
      setBatches((prev) => prev.map((b) => (b.id === batchId ? updated : b)));
    } catch (e) {
      setRetryError({ batchId, message: e instanceof Error ? e.message : String(e) });
    } finally {
      setRetryingId(null);
    }
  }

  return (
    <section
      className="border rounded p-4 bg-rh-card space-y-3"
      aria-label={t('webAdmin.auditBatches.ariaLabel', { locale: 'cs' })}
      data-testid="audit-batches-section"
    >
      <h2 className="font-medium">{t('webAdmin.auditBatches.heading', { locale: 'cs' })}</h2>
      {loading && <p role="status">{t('webAdmin.auditBatches.loading', { locale: 'cs' })}</p>}
      {error !== null && (
        <p role="alert" className="text-sm text-red-600">
          {t('webAdmin.auditBatches.loadErrorTitle', { locale: 'cs' })}: {error}
        </p>
      )}
      {!loading && error === null && batches.length === 0 && (
        <p className="text-sm text-neutral-500">{t('webAdmin.auditBatches.empty', { locale: 'cs' })}</p>
      )}
      {batches.length > 0 && (
        <ul className="text-sm space-y-2" role="list">
          {batches.map((batch) => (
            <li key={batch.id} className="border rounded px-3 py-2" data-testid="audit-batch-row">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rh-badge rh-badge--neutral">{BATCH_STATUS_LABELS[batch.status]}</span>
                <span>
                  {t('webAdmin.auditBatches.progressLabel', {
                    locale: 'cs',
                    values: { done: String(batch.doneCount), total: String(batch.totalCount) },
                  })}
                  {batch.failedCount > 0
                    ? t('webAdmin.auditBatches.failedSuffix', {
                        locale: 'cs',
                        values: { failed: String(batch.failedCount) },
                      })
                    : ''}
                </span>
                <span className="text-xs text-neutral-500">
                  {new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' }).format(
                    new Date(batch.createdAt),
                  )}
                </span>
                <button
                  type="button"
                  className="text-xs underline"
                  onClick={() => { setExpandedId((prev) => (prev === batch.id ? null : batch.id)); }}
                  data-testid="audit-batch-toggle"
                >
                  {t('webAdmin.auditBatches.itemsToggle', { locale: 'cs' })}
                </button>
                {batch.items.some((i) => i.status === 'FAILED' && i.auditRunId !== null) && (
                  <button
                    type="button"
                    className="rh-btn text-xs"
                    disabled={retryingId === batch.id}
                    onClick={() => void retryBatch(batch.id)}
                    data-testid="audit-batch-retry"
                  >
                    {retryingId === batch.id
                      ? t('webAdmin.auditBatches.retrying', { locale: 'cs' })
                      : t('webAdmin.auditBatches.retryFailed', { locale: 'cs' })}
                  </button>
                )}
              </div>
              {batch.failedCount > 0 && (
                <p className="mt-1 text-xs text-neutral-600" data-testid="audit-batch-failure-reasons">
                  {t('webAdmin.auditBatches.failureReasonsLabel', { locale: 'cs' })}:{' '}
                  {/* Krok FUNK1 bod 3: kod ze serveru („unsupported_property_kind")
                      prochazi katalogem — na obrazovce stoji veta, ne enum.
                      Duvody se opakuji, proto `Set`: dvakrat tataz veta nic nerekne. */}
                  {[
                    ...new Set(
                      batch.items
                        .filter((i) => i.status === 'FAILED')
                        .map((i) => failureReasonSentence(i.failureReason)),
                    ),
                  ].join(' ')}
                </p>
              )}
              {retryError !== null && retryError.batchId === batch.id && (
                <p role="alert" className="mt-1 text-xs text-red-600">
                  {t('webAdmin.auditBatches.retryErrorPrefix', { locale: 'cs' })}: {retryError.message}
                </p>
              )}
              {expandedId === batch.id && (
                <ul className="mt-2 ml-4 space-y-1" role="list">
                  {batch.items.map((item) => (
                    <li key={item.propertyId} className="flex items-center gap-2 text-xs">
                      <span className="rh-badge rh-badge--neutral">{captionOrPreparing(AUDIT_RUN_STATUS_CAPTIONS[item.status], item.status)}</span>
                      <Link to="/nemovitosti/$id" params={{ id: item.propertyId }} className="underline">
                        {t('webAdmin.auditBatches.itemPropertyLink', { locale: 'cs' })}
                      </Link>
                      {item.failureReason !== null && (
                        <span className="text-neutral-500">({failureReasonSentence(item.failureReason)})</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function AuditPage(): React.JSX.Element {
  const [entityType, setEntityType] = React.useState('');
  const [entityId, setEntityId] = React.useState('');
  const [action, setAction] = React.useState('');
  const [traceId, setTraceId] = React.useState('');
  const [events, setEvents] = React.useState<AuditEvent[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [anchors, setAnchors] = React.useState<Anchor[]>([]);
  const [verifyResult, setVerifyResult] = React.useState<string | null>(null);

  const fetchEvents = React.useCallback(
    async (cursor?: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (entityType) params.set('entityType', entityType);
        if (entityId) params.set('entityId', entityId);
        if (action) params.set('action', action);
        if (traceId) params.set('traceId', traceId);
        params.set('limit', '50');
        if (cursor) params.set('cursor', cursor);
        const res = await fetch(`${baseUrl}/v1/admin/audit/events?${params.toString()}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { items: AuditEvent[]; nextCursor: string | null };
        if (cursor) setEvents((prev) => [...prev, ...data.items]);
        else setEvents(data.items);
        setNextCursor(data.nextCursor);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [entityType, entityId, action, traceId],
  );

  const fetchAnchors = React.useCallback(async () => {
    try {
      const res = await fetch(`${baseUrl}/v1/admin/audit/anchors`, { credentials: 'include' });
      if (!res.ok) return;
      const data = (await res.json()) as { items: Anchor[] };
      setAnchors(data.items);
    } catch {
      // ignore
    }
  }, []);

  const handleExport = React.useCallback(
    async (format: 'json' | 'csv') => {
      const params = new URLSearchParams();
      if (entityType) params.set('entityType', entityType);
      if (entityId) params.set('entityId', entityId);
      if (action) params.set('action', action);
      if (traceId) params.set('traceId', traceId);
      params.set('format', format);
      const res = await fetch(`${baseUrl}/v1/admin/audit/events/export?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setError(t('webAdmin.auditLog.exportFailedPrefix', { locale: 'cs', values: { status: String(res.status) } }));
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-export.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [entityType, entityId, action, traceId],
  );

  const handleVerify = React.useCallback(async () => {
    setVerifyResult(t('webAdmin.auditLog.verifying', { locale: 'cs' }));
    try {
      const res = await fetch(`${baseUrl}/v1/admin/audit/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ limit: 200 }),
      });
      const data = (await res.json()) as { ok: boolean; totalEvents: number; broken: unknown[] };
      setVerifyResult(
        data.ok
          ? t('webAdmin.auditLog.verifyOk', { locale: 'cs', values: { totalEvents: String(data.totalEvents) } })
          : t('webAdmin.auditLog.verifyBroken', { locale: 'cs', values: { count: String(data.broken.length) } }),
      );
    } catch (e) {
      setVerifyResult(e instanceof Error ? e.message : String(e));
    }
  }, []);

  React.useEffect(() => {
    void fetchAnchors();
  }, [fetchAnchors]);

  /*
   * Krok UXF4 bod 7 (#40, N11): vychozi pohled uz neni filtr, do ktereho se
   * musi psat `entityType` a UUID — udalosti se nactou hned a stoji na
   * obrazovce lidskou vetou. Je to TOTEZ volani jako „Hledat" bez filtru.
   */
  // Jen pri prvnim zobrazeni: dal rozhoduje „Hledat" ve „Pro správce" —
  // `fetchEvents` meni identitu s kazdym pismenem ve filtru a bez teto pojistky
  // by kazde z nich poslalo dotaz.
  const firstLoadDone = React.useRef(false);
  React.useEffect(() => {
    if (firstLoadDone.current) return;
    firstLoadDone.current = true;
    void fetchEvents(null);
  }, [fetchEvents]);

  /** Lidsky pohled: jen zmeny, ktere udelal clovek (zadani bodu 7). */
  const userRows = React.useMemo(
    () => events.filter(isUserChange).map((event) => ({ id: event.id, ...auditHumanRow(event) })),
    [events],
  );

  return (
    <div className="space-y-6" data-testid="audit-page">
      <h1 className="text-2xl font-bold">{t('webAdmin.auditLog.heading', { locale: 'cs' })}</h1>

      <AuditBatchesSection />

      <section
        className="border rounded p-4 bg-rh-card space-y-3"
        aria-label={t('webAdmin.auditLog.human.viewHeading', { locale: 'cs' })}
        data-testid="audit-human-section"
      >
        <h2 className="font-medium">{t('webAdmin.auditLog.human.viewHeading', { locale: 'cs' })}</h2>
        <p className="text-sm text-neutral-600">{t('webAdmin.auditLog.human.viewHint', { locale: 'cs' })}</p>
        {loading && <p role="status">{t('webAdmin.auditBatches.loading', { locale: 'cs' })}</p>}
        {!loading && userRows.length === 0 && (
          <p className="text-sm text-neutral-500" data-testid="audit-human-empty">
            {t('webAdmin.auditLog.human.empty', { locale: 'cs' })}
          </p>
        )}
        {userRows.length > 0 && (
          <ul className="space-y-2 text-sm" role="list" data-testid="audit-human-list">
            {userRows.map((row) => (
              <li key={row.id} className="border rounded px-3 py-2" data-testid="audit-human-row">
                <p className="font-medium">{row.sentence}</p>
                <p className="text-xs text-neutral-500">
                  {row.actor} · {row.when}
                </p>
                {row.reason !== null && <p className="text-xs text-neutral-600">{row.reason}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/*
       * „Pro správce" (zadani bodu 7): UUID, typy udalosti, otisky retezu,
       * mesicni kotvy a export. Tentyz vzor jako `portal-technical-*`
       * v `portaly.tsx` — nativni `<details>`, zadna nova komponenta.
       */}
      <details className="space-y-3" data-testid="audit-admin-details">
        <summary className="cursor-pointer font-medium">
          {t('webAdmin.auditLog.human.adminToggle', { locale: 'cs' })}
        </summary>
        <p className="mt-2 text-sm text-neutral-600">{t('webAdmin.auditLog.human.adminHint', { locale: 'cs' })}</p>

      <section className="border rounded p-4 bg-rh-card space-y-3" aria-label={t('webAdmin.auditLog.filterAriaLabel', { locale: 'cs' })}>
        <h2 className="font-medium">{t('webAdmin.auditLog.filterHeading', { locale: 'cs' })}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="flex flex-col text-sm">
            {t('webAdmin.auditLog.entityTypeLabel', { locale: 'cs' })}
            <input
              data-testid="audit-filter-entityType"
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              placeholder={t('webAdmin.auditLog.entityTypePlaceholder', { locale: 'cs' })}
              className="border rounded px-2 py-1"
            />
          </label>
          <label className="flex flex-col text-sm">
            {t('webAdmin.auditLog.entityIdLabel', { locale: 'cs' })}
            <input
              data-testid="audit-filter-entityId"
              value={entityId}
              onChange={(e) => setEntityId(e.target.value)}
              placeholder={t('webAdmin.auditLog.entityIdPlaceholder', { locale: 'cs' })}
              className="border rounded px-2 py-1"
            />
          </label>
          <label className="flex flex-col text-sm">
            {t('webAdmin.auditLog.actionLabel', { locale: 'cs' })}
            <input
              data-testid="audit-filter-action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder={t('webAdmin.auditLog.actionPlaceholder', { locale: 'cs' })}
              className="border rounded px-2 py-1"
            />
          </label>
          <label className="flex flex-col text-sm">
            {t('webAdmin.auditLog.traceIdLabel', { locale: 'cs' })}
            <input
              data-testid="audit-filter-traceId"
              value={traceId}
              onChange={(e) => setTraceId(e.target.value)}
              placeholder={t('webAdmin.auditLog.traceIdPlaceholder', { locale: 'cs' })}
              className="border rounded px-2 py-1"
            />
          </label>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="audit-search" onClick={() => void fetchEvents(null)} className="px-4 py-1 bg-neutral-900 text-rh-on-accent rounded text-sm" disabled={loading}>
            {t('webAdmin.auditLog.search', { locale: 'cs' })}
          </button>
          <button type="button" data-testid="audit-export-json" onClick={() => void handleExport('json')} className="px-4 py-1 border rounded text-sm">
            {t('webAdmin.auditLog.exportJson', { locale: 'cs' })}
          </button>
          <button type="button" data-testid="audit-export-csv" onClick={() => void handleExport('csv')} className="px-4 py-1 border rounded text-sm">
            {t('webAdmin.auditLog.exportCsv', { locale: 'cs' })}
          </button>
        </div>
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      </section>

      <section className="border rounded p-4 bg-rh-card space-y-3" aria-label={t('webAdmin.auditLog.eventsAriaLabel', { locale: 'cs' })}>
        <h2 className="font-medium">{t('webAdmin.auditLog.eventsHeading', { locale: 'cs', values: { count: String(events.length) } })}</h2>
        {/* Krok FUNK1 bod 2: seznam udalosti pres sdilenou `ListTable` —
            na 390 px mel sloupec „kdo" 38 px a text se lamal po pismenech. */}
        <div className="overflow-auto max-h-[400px]">
          <ListTable
            caption={t('webAdmin.auditLog.eventsAriaLabel', { locale: 'cs' })}
            rows={events}
            rowKey={(e) => e.id}
            rowTestId={() => 'audit-row'}
            empty={<span className="text-neutral-500">{t('webAdmin.auditLog.noEvents', { locale: 'cs' })}</span>}
            columns={[
              {
                key: 'seq',
                label: t('webAdmin.auditLog.columnSeq', { locale: 'cs' }),
                cell: (e) => <span className="font-mono text-xs">{e.seq}</span>,
              },
              {
                key: 'time',
                label: t('webAdmin.auditLog.columnTime', { locale: 'cs' }),
                cell: (e) => (
                  <span className="text-xs">
                    {new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium', timeStyle: 'short' }).format(
                      new Date(e.occurredAt),
                    )}
                  </span>
                ),
              },
              {
                key: 'actor',
                label: t('webAdmin.auditLog.columnActor', { locale: 'cs' }),
                cell: (e) => (
                  <>
                    {e.actorId ?? e.actorType} <span className="text-neutral-500">{e.actorRole}</span>
                    {e.onBehalfOf !== null && (
                      <span className="text-xs text-amber-700">
                        {t('webAdmin.auditLog.onBehalfOfPrefix', { locale: 'cs', values: { onBehalfOf: e.onBehalfOf } })}
                      </span>
                    )}
                  </>
                ),
              },
              {
                key: 'action',
                label: t('webAdmin.auditLog.columnAction', { locale: 'cs' }),
                cell: (e) => <span className="font-mono text-xs">{e.action}</span>,
              },
              {
                key: 'entity',
                label: t('webAdmin.auditLog.columnEntity', { locale: 'cs' }),
                cell: (e) => (
                  <span className="text-xs">
                    {e.entityType}:{e.entityId?.slice(0, 8)}
                  </span>
                ),
              },
              {
                key: 'reason',
                label: t('webAdmin.auditLog.columnReason', { locale: 'cs' }),
                cell: (e) => <span className="text-xs">{e.reason}</span>,
              },
              {
                key: 'hash',
                label: t('webAdmin.auditLog.columnHash', { locale: 'cs' }),
                cell: (e) => (
                  <span className="font-mono text-xs" title={e.hash}>
                    {e.hash.slice(0, 8)}…
                  </span>
                ),
              },
            ]}
          />
        </div>
        {nextCursor && (
          <button type="button" data-testid="audit-next-page" onClick={() => void fetchEvents(nextCursor)} className="px-4 py-1 border rounded text-sm" disabled={loading}>
            {t('webAdmin.auditLog.nextPage', { locale: 'cs' })}
          </button>
        )}
      </section>

      <section className="border rounded p-4 bg-rh-card space-y-3" aria-label={t('webAdmin.auditLog.anchorsAriaLabel', { locale: 'cs' })}>
        <h2 className="font-medium">{t('webAdmin.auditLog.anchorsHeading', { locale: 'cs' })}</h2>
        <div className="flex gap-2">
          <button type="button" data-testid="audit-verify" onClick={() => void handleVerify()} className="px-4 py-1 border rounded text-sm">
            {t('webAdmin.auditLog.verifyChain', { locale: 'cs' })}
          </button>
          {verifyResult && <span data-testid="audit-verify-result" className="text-sm">{verifyResult}</span>}
        </div>
        {anchors.length > 0 && (
          <ul className="text-sm space-y-1" role="list">
            {anchors.map((a) => (
              <li key={a.id} className="font-mono text-xs border rounded px-2 py-1">
                {a.year}-{String(a.month).padStart(2, '0')}: {a.anchorHash.slice(0, 16)}…
                {t('webAdmin.auditLog.anchorEventCountSuffix', { locale: 'cs', values: { count: String(a.eventCount) } })}
                {a.wormPath ? t('webAdmin.auditLog.wormCheck', { locale: 'cs' }) : ''}
              </li>
            ))}
          </ul>
        )}
      </section>
      </details>
    </div>
  );
}
